import type { GuaranteeStatus } from '../types/guarantee.types';

/**
 * Where an AR Live Guarantee claim is made.
 *
 * Same address as the `supportEmail` const in `pages/TermsOfServicePage.tsx`
 * and `pages/PrivacyPolicyPage.tsx`, which are the binding statements of this
 * promise. If the address ever changes it must change in all three.
 */
export const GUARANTEE_CLAIM_EMAIL = 'support@arspaceplatform.com';

/**
 * The guarantee as a merchant reads it.
 *
 * `headline` is the promise; `detail` is the mechanics. Both are plain strings
 * so the two surfaces that render this (`PricingSection4`, `PostTrialBanner`)
 * cannot drift apart on a wording change.
 */
export interface GuaranteeCopy {
  headline: string;
  detail: string;
}

/**
 * Copy for a merchant with a frozen target.
 *
 * `target_views` is read verbatim — it was committed at subscribe time and a
 * client-side adjustment would show the merchant a different promise than the
 * one we adjudicate against.
 */
export function guaranteeCopyFor(guarantee: GuaranteeStatus): GuaranteeCopy {
  const target = guarantee.target_views;
  const views = target === 1 ? 'AR view' : 'AR views';

  // `used_floor` means the shop had too little history to be measured against
  // itself, so the promise is a flat number. Claiming an uplift over a
  // baseline they never produced would be inventing a comparison.
  const headline = guarantee.used_floor
    ? `Reach ${target} ${views} in your first 60 days, or we refund every dollar you paid for those 60 days — and you keep the models.`
    : `Beat your own traffic: ${target} ${views} in your first 60 days — ${
        guarantee.uplift_percent !== null ? `${guarantee.uplift_percent}% above ` : 'above '
      }the ${guarantee.baseline_views} you did in the 60 days before you subscribed. Miss it and we refund every dollar you paid for those 60 days — and you keep the models.`;

  // The remedy is manual and always has been. Wording that implies an
  // automatic refund would contradict the Terms this promise is bound by.
  const detail = guarantee.claimable
    ? `Your 60 days have closed at ${guarantee.current_views} of ${target} ${views}. Email ${GUARANTEE_CLAIM_EMAIL} from your store's address to claim — refunds are reviewed and issued by a person, never automatically.`
    : `You're at ${guarantee.current_views} of ${target} ${views}${
        guarantee.window_open ? ` with ${guarantee.days_remaining} days to go` : ''
      }. If you fall short, email ${GUARANTEE_CLAIM_EMAIL} to claim — refunds are reviewed and issued by a person, never automatically.`;

  return { headline, detail };
}

/**
 * Copy for a merchant who has not subscribed yet.
 *
 * Deliberately carries NO view count. The target is computed from the shop's
 * own traffic at the moment they subscribe, so no number exists to promise
 * here — and quoting a placeholder would state a figure we would then not
 * honour. Describing how the target is set is the honest version of the same
 * risk reversal.
 */
export const GUARANTEE_PRESUBSCRIBE_COPY: GuaranteeCopy = {
  headline:
    'When you subscribe we set your store an AR view target for your first 60 days, calculated from your own storefront traffic. Miss it and we refund every dollar you paid for those 60 days — and you keep the models.',
  detail: `You'll see your exact target here the moment your plan is active, and it never changes after that. Claims are made by emailing ${GUARANTEE_CLAIM_EMAIL} — refunds are reviewed and issued by a person, never automatically.`,
};
